/**
 * Phase R8 Feed Health types — the 6-state taxonomy from CD R8 spec.
 *
 * Five of the six states map to existing --bm-* semantic tokens; the
 * 6th (BLOCKED) earns its own --bm-blocked token (calm slate-violet,
 * deliberately NOT alarming red — a structural gap to route around,
 * not an active fire). See feedhealth-tokens.css.
 *
 * Honesty rails per CD R8:
 *   - Each state ships icon + word + colour (CVD-safe, never colour alone)
 *   - BLOCKED carries a `reason` attribute (the "permanence" — why it's
 *     structurally blocked), distinct from transient DEGRADED
 *   - 30-day per-source history strip uses the same state palette
 *   - Coverage feeds (Phase 5 GDELT etc.) live in a SEPARATE tab from
 *     surveillance sources — different operational meaning
 */

import type { BlockedReason, SourceHealth } from '@biomax/plugin-surveillance-feed';

/** 6-state taxonomy. Derived from SourceHealth flags via `deriveState()`. */
export type FeedState = 'live' | 'idle' | 'stale' | 'rotting' | 'degraded' | 'blocked';

/** Per-state display metadata — icon + word + colour token + sort rank.
 *  `transient` is true for fail-states that may recover (degraded / rotting /
 *  stale), false for structural (blocked), null for non-fail (live / idle). */
export const STATE_META: Record<FeedState, {
  word: string;
  icon: string;
  cssVar: string;
  bgVar: string;
  rank: number;
  transient: boolean | null;
}> = {
  live:     { word: 'LIVE',     icon: '✓', cssVar: 'var(--bm-state-live)',     bgVar: 'var(--bm-state-live-bg)',     rank: 0, transient: null },
  idle:     { word: 'IDLE',     icon: '◦', cssVar: 'var(--bm-state-idle)',     bgVar: 'var(--bm-state-idle-bg)',     rank: 1, transient: null },
  stale:    { word: 'STALE',    icon: '⏳', cssVar: 'var(--bm-state-stale)',    bgVar: 'var(--bm-state-stale-bg)',    rank: 3, transient: true },
  rotting:  { word: 'ROTTING',  icon: '◷', cssVar: 'var(--bm-state-rotting)',  bgVar: 'var(--bm-state-rotting-bg)',  rank: 4, transient: true },
  degraded: { word: 'DEGRADED', icon: '⚠', cssVar: 'var(--bm-state-degraded)', bgVar: 'var(--bm-state-degraded-bg)', rank: 5, transient: true },
  blocked:  { word: 'BLOCKED',  icon: '🔒', cssVar: 'var(--bm-state-blocked)',  bgVar: 'var(--bm-state-blocked-bg)',  rank: 6, transient: false },
};

/** Why a source is BLOCKED — display metadata. */
export const REASON_META: Record<BlockedReason, { word: string; detail: string }> = {
  antibot:     { word: 'Anti-bot', detail: 'Cloudflare-class bot protection; bypass requires browser-rendering infrastructure (Puppeteer) or a partnership data feed.' },
  partnership: { word: 'Partnership-gated', detail: 'Access requires a data-sharing agreement / credentials not yet in place.' },
  sanctions:   { word: 'Sanctions', detail: 'Jurisdiction under sanctions; direct ingest not permitted — await cleared alternative source.' },
  geo:         { word: 'Geo-firewall', detail: 'Source firewalls non-domestic IPs; needs in-region egress or a mirror.' },
};

/** Derive the canonical FeedState from a SourceHealth row.
 *  Priority order: blocked → degraded → rotting → stale → idle → live.
 *  BLOCKED takes precedence over transient failures (a blocked source that
 *  also happens to fail is still STRUCTURALLY blocked, not a 503).
 *  IDLE = OK + no signals yet. LIVE = OK + signals + not stale. */
export function deriveState(h: SourceHealth): FeedState {
  if (h.blocked) return 'blocked';
  if (!h.ok) return 'degraded';
  if (h.rotting) return 'rotting';
  if (h.stale) return 'stale';
  if (h.signalCount === 0) return 'idle';
  return 'live';
}

/** WHO regions — for the persona-style region lens. */
export const WHO_REGIONS: ReadonlyArray<string> = ['AFRO', 'AMRO', 'EMRO', 'EURO', 'SEARO', 'WPRO', 'Global'];
